/* Interaction sounds: the same cues, materials, loudness and start-up as sayso.co
   (site-astro: BaseLayout, Modal, SayingCarousel, sayso-search), so both sites sound alike.
   - cuelume@0.2.8 (assets/cuelume-0.2.8.esm.js, the site's version) synthesizes every cue
     with Web Audio: there are no audio files to download.
   - No AudioContext until the first press or key. Browsers only start audio inside a real
     user activation: a key, a mouse press, or the END of a finger tap (pointerup / touchend /
     click), never a finger's pointerdown. So every activating event (re)starts audio: the
     first success plays one silent tap to build cuelume's bus (80ms at volume 0), and later
     ones resume a context iPhone Safari suspended (call, lock screen, app switch, back/forward
     cache). Cues fired while that runs wait for it (the site's __saysoWhenAudio). A press cue
     from a finger's first pointerdown (before the page is activated) is held and plays when
     that tap ends, instead of vanishing.
   - Level: x1.5 gain in front of the speakers (SOUND_BOOST; the site's x2 made short clicks
     harsh, cuelume's own level was too quiet on phones), then a hard clamp at full scale, so
     the output can never go past 1.0 (no clipping) whatever the cue.
   - Input, as cuelume.dev does: each cue is told what it came from (touch, mouse, pen,
     keyboard), so a finger tap gets cuelume's softer touch tap. Cues fired from code (the "1"
     landing, the price roll) use the last press or key if it was under 1.5s ago.
   - Like the site: no hover sounds on touch, nothing on load, reduced motion doesn't mute
     (cuelume: motion setting, not a sound setting).
   - Off switch: localStorage "sayso-sound" = "off" (the site has no toggle yet).
     window.saysoSound.setEnabled(false|true) sets and stores it.
   Cues (site -> shop):
     menu/panel open  select·press   -> menu + bag drawer open (site-panel:open)
     menu/panel close select·mech    -> menu + bag drawer close (site-panel:close)
     list link hover  tap·press      -> menu + search-result links (mouse only; not add-to-cart)
     list link press  select·press   -> menu + search-result links, on press (and Enter on a menu link)
     button click     tap·mech       -> add-to-cart + add another hoodie, on press: a short key click
                                        (~20ms), so the tap reads as pressed; the "1" landing
                                        (count·mech) follows
     button hover     tap·press      -> every button (mouse only), except add-to-cart (Lane, 3 Nov)
     other buttons    CLICK_CUES     -> one table below (summary row fold, image view, bag qty +/−,
                                        remove / delete, undo, checkout; any new button: tap·mech)
     search typing    type·mech      -> menu search field
     prev/next        close·mech (‹) / open·mech (›) -> saying ‹ › / swipe / arrow keys (script.js cyclePrint)
     pick from list   select·press   -> colour + size choices (script.js)
     gallery counter  count·mech 300 -> bag count when the flying "1" lands (sayso-fly-to-bag.js)
     price change     count·mech     -> summary rows / footer prices roll (script.js), via .price():
                                        ONE count per visible roll, stretched to the roll (cuelume:
                                        a count for a rolling number); falls ("back") when prices
                                        drop, rises ("forward") when they go up. Rows + footer rolling
                                        together share it. A "1" landing still sounding: it starts
                                        40ms after, for what is left of the roll (skipped if that is
                                        under 240ms). .priceCut(ms): the price went out of sight, so
                                        a cue not started yet is dropped (one already playing ends
                                        on its own: cuelume has no stop).
   Public: window.saysoSound.play(cue, options), .setEnabled(on), .enabled(); and the site's
   names window.__saysoPlay, saysoMenuSound(open), saysoListSound(kind), __saysoWhenAudio. */
// Relative: resolves next to this file on the CDN (the file name carries the version).
import { play, unlock, setVolume, setEnabled } from "./cuelume-0.2.8.esm.js";

const STORE_KEY = "sayso-sound";
const SOUND_BOOST = 1.5; // gain in front of the speakers (sayso.co uses 2; cuelume alone is 1)

if (!window.saysoSound) {
  // Every context cuelume makes sends its output through boost -> clamp -> speakers.
  const AC_NAME = window.AudioContext ? "AudioContext" : "webkitAudioContext";
  const AC = window[AC_NAME];
  if (AC) {
    window[AC_NAME] = class extends AC {
      #out;
      get destination() {
        return (this.#out ??= (() => {
          const boost = this.createGain();
          boost.gain.value = SOUND_BOOST;
          // Identity inside [-1, 1], flat outside: the boost can never push a sample past 1.0.
          const clamp = this.createWaveShaper();
          clamp.curve = new Float32Array([-1, 1]);
          boost.connect(clamp).connect(super.destination);
          return boost;
        })());
      }
    };
  }

  const stored = () => {
    try { return localStorage.getItem(STORE_KEY); } catch { return null; }
  };
  let on = stored() !== "off";
  setEnabled(on);

  const log = (entry) => {
    if (Array.isArray(window.__saysoSoundLog)) window.__saysoSoundLog.push({ ...entry, t: Math.round(performance.now()) });
  };
  // When the last count (the "1" landing) stops sounding (performance.now ms), so the
  // price cue can start after it: a new count would fade it out (cuelume: one voice per cue).
  let otherUntil = 0;
  // What the last press or key came from (cuelume input: touch / mouse / pen / keyboard).
  const INPUT_RECENT_MS = 1500;
  let lastInput = null; // { input, at }
  const noteInput = (input) => { lastInput = { input, at: performance.now() }; };
  document.addEventListener("pointerdown", (e) => {
    noteInput(e.pointerType === "touch" || e.pointerType === "pen" ? e.pointerType : "mouse");
  }, { capture: true, passive: true });
  document.addEventListener("keydown", (e) => { if (!e.repeat) noteInput("keyboard"); }, true);
  const withInput = (options) => {
    if (options?.input || !lastInput || performance.now() - lastInput.at > INPUT_RECENT_MS) return options;
    return { ...options, input: lastInput.input };
  };
  const fire = (cue, options, kind, extra) => {
    if (!on) return;
    log({ cue, ...options, ...(kind ? { kind } : {}), ...extra });
    try { play(cue, options); } catch {}
  };

  // Audio start-up (see the top). unlock() is cheap once running (no new context, no resume).
  const activated = () => navigator.userActivation?.hasBeenActive !== false;
  let warm = false; // cuelume's bus built (silent tap played)
  let starting = null; // start-up in flight: cues wait for it
  const HOLD_MS = 1000;
  let held = []; // press cues from before activation: { cue, opts, at }
  const flushHeld = () => {
    const now = performance.now();
    const due = held.filter((h) => now - h.at < HOLD_MS);
    held = [];
    due.forEach((h) => fire(h.cue, h.opts));
  };
  const whenAudio = (fn) => (starting ? starting.then(fn) : fn());
  const startAudio = () => {
    if (!on || starting || !activated()) return;
    starting = unlock()
      .then((running) => {
        if (!running || warm) return;
        warm = true;
        try { setVolume(0); play("tap", { theme: "default" }); } catch {}
        return new Promise((done) => setTimeout(() => { try { setVolume(1); } catch {} done(); }, 80));
      })
      .catch(() => {})
      .finally(() => { starting = null; flushHeld(); });
  };
  for (const type of ["pointerdown", "pointerup", "touchend", "click", "keydown"]) {
    document.addEventListener(type, startAudio, { capture: true, passive: true });
  }

  // hold: a press cue (pointerdown) may come before the tap that activates the page; keep it
  // for that tap. Other cues before activation (mouse hover) are just skipped.
  const sound = (cue, options, hold = false) => {
    if (on && cue === "count") otherUntil = Math.max(otherUntil, performance.now() + (options?.duration ?? 800));
    const opts = withInput(options);
    if (on && hold && !activated()) {
      held.push({ cue, opts, at: performance.now() });
      return Promise.resolve();
    }
    return whenAudio(() => fire(cue, opts));
  };

  // Price change: one count·mech per visible roll, however many prices roll (rows and footer
  // call this in the same frame or two: the first plays, the rest join it). It shares
  // cuelume's "count" voice with the "1" landing, so it waits for a landing still sounding.
  // replace: a new roll replacing one still running gets its own cue (it fades the old one).
  const PRICE_GAP_MS = 40;
  const PRICE_MIN_MS = 240; // less roll than this left: no cue (cuelume's shortest count is 300ms)
  let price = null; // { id, startAt, until, timer, started }
  let priceId = 0;
  const dropPending = (why) => {
    if (!price || price.started) return;
    clearTimeout(price.timer);
    log({ cue: "count", kind: "price-drop", id: price.id, why });
    price = null;
  };
  const priceSound = ({ duration = 380, direction = "back", replace = false } = {}) => {
    if (!on) return;
    const now = performance.now();
    if (price && now < price.until && !replace) return log({ cue: "count", kind: "price-join", id: price.id, duration: Math.round(duration) });
    dropPending("replaced");
    const wait = otherUntil > now ? otherUntil - now + PRICE_GAP_MS : 0;
    const left = duration - wait;
    if (left < PRICE_MIN_MS) return log({ cue: "count", kind: "price", skipped: wait ? "landing" : "short", duration: Math.round(duration) });
    const options = withInput({ theme: "mech", duration: Math.round(left), direction });
    const entry = { id: ++priceId, startAt: now + wait, until: now + duration, timer: null, started: false };
    price = entry;
    const go = () => {
      if (price !== entry || !on) return;
      entry.started = true;
      whenAudio(() => fire("count", options, "price", { id: entry.id }));
    };
    if (wait) entry.timer = setTimeout(go, wait);
    else go();
  };
  // The price goes out of sight in ms (footer sinking, rows scrolled away): a cue still
  // waiting is dropped unless PRICE_MIN_MS of it would be seen. One already playing can't be
  // stopped (cuelume has no stop) and is short; it is left to finish.
  const priceCut = (ms = 0) => {
    const now = performance.now();
    if (!price || now >= price.until) return;
    if (price.started) return log({ cue: "count", kind: "price-cut", id: price.id, playing: true });
    if (now + ms - price.startAt < PRICE_MIN_MS) dropPending("cut");
  };
  const menuSound = (open) => sound("select", { theme: open ? "press" : "mech" });
  const listSound = (kind) => sound(kind, { theme: "press" });

  // Menu and bag drawer (assets/site-panels.js). Capture: the panel events don't bubble.
  // Haptics on the same moments as sayso.co (window.saysoHaptic from script.js; works with sound off).
  document.addEventListener("site-panel:open", () => { menuSound(true); window.saysoHaptic?.("light"); }, true);
  document.addEventListener("site-panel:close", (e) => { if (e.detail?.quiet) return; menuSound(false); window.saysoHaptic?.("light"); }, true);

  // Every control's sound, in one place. Three kinds:
  // - PRESS: sounds on pointerdown so it reads as pressed: menu / search-result links
  //   (select·press) and add-to-cart / add another hoodie (tap·mech).
  // - VOICED_ELSEWHERE: played by the code that handles them, for every input (tap, key,
  //   swipe): colour, size, prev/next (script.js), the × on a summary row (script.js), menu
  //   and bag open / close (site-panel events above), the page overlay's × (sayso-page-overlay.js).
  // - Every other button: CLICK_CUES on click (also Enter / Space), first match wins.
  // Hover (mouse only): tap·press on links in the menu and every button except add-to-cart
  // (no ATC hover, Lane 3 Nov: its press click is enough).
  const CART_BUTTON = ".alpine-cart-button:not(.alpine-cart-status-button), .alpine-add-hoodie";
  const PRESS = `.site-menu a, .cart-empty-cta, ${CART_BUTTON}`; // .cart-empty-cta: empty bag's "Build your hoodie" link
  const VOICED_ELSEWHERE = [
    ".alpine-colour-dot", ".alpine-size-choice", ".alpine-step-button", ".alpine-hoodie-remove",
    "[data-panel-open]", "[popovertarget]", ".page-overlay-close",
  ].join(", ");
  const CLICK_CUES = [
    // Summary row: opens (press) / folds (mech) its hoodie, like the menu and bag.
    [".alpine-hoodie-toggle", (el) => ["select", { theme: el.getAttribute("aria-expanded") === "true" ? "mech" : "press" }]],
    [".alpine-image-view-button", () => ["select", { theme: "press" }]],
    // Out of the bag: the same mech close as the × on a summary row.
    ["[data-cart-control='remove'], .alpine-cart-status-delete", () => ["close", { theme: "mech" }]],
    [".alpine-cart-status-undo", () => ["select", { theme: "mech" }]],
    // Bag qty − / + and checkout: the add-to-cart key click.
    ["[data-cart-control='dec'], [data-cart-control='inc'], .cart-checkout-button", () => ["tap", { theme: "mech" }]],
    ["button", () => ["tap", { theme: "mech" }]],
  ];
  const HOVER = `.site-menu a, .cart-empty-cta, button:not(${CART_BUTTON}), [data-panel-open]`;
  document.addEventListener("pointerenter", (e) => {
    if (e.pointerType === "mouse" && e.target.matches?.(HOVER) && !e.target.disabled) listSound("tap");
  }, true);
  document.addEventListener("pointerdown", (e) => {
    const el = e.target.closest?.(PRESS);
    if (el && !el.disabled) el.matches(CART_BUTTON) ? sound("tap", { theme: "mech" }, true) : sound("select", { theme: "press" }, true);
    if (el?.matches(".site-menu a")) window.saysoHaptic?.("selection");
  }, true);
  document.addEventListener("click", (e) => {
    const el = e.target.closest?.("button");
    if (!el || el.disabled || el.matches(VOICED_ELSEWHERE)) return;
    if (el.matches(PRESS)) {
      // Enter / Space (no pointer: detail 0) on add-to-cart / add another: the press click.
      if (e.detail === 0 && el.matches(CART_BUTTON)) sound("tap", { theme: "mech" });
      return;
    }
    const [, cueFor] = CLICK_CUES.find(([sel]) => el.matches(sel));
    sound(...cueFor(el));
  }, true);
  document.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.repeat && document.activeElement?.closest?.(".site-menu a, .cart-empty-cta")) listSound("select");
  }, true);
  document.addEventListener("input", (e) => {
    if (e.target.matches?.(".site-search-input")) sound("type", { theme: "mech" });
  }, true);

  window.saysoSound = {
    play: sound,
    price: priceSound,
    priceCut,
    enabled: () => on,
    setEnabled(next) {
      on = Boolean(next);
      setEnabled(on);
      if (!on) dropPending("off");
      try { on ? localStorage.removeItem(STORE_KEY) : localStorage.setItem(STORE_KEY, "off"); } catch {}
      if (on) startAudio();
    },
  };
  window.__saysoPlay = sound;
  window.__saysoWhenAudio = whenAudio;
  window.saysoMenuSound = menuSound;
  window.saysoListSound = listSound;
}
